import { useQuery } from '@tanstack/react-query';
import { ProductDetail } from '@/types/product';
import useStrapiHome from './useStrapiHome';

// Función para obtener un producto específico de PrestaShop
const fetchProductById = async (id: number, lang: string = 'es'): Promise<ProductDetail | null> => {
  try {
    const response = await fetch(`/api/${lang}/products/${id}`);
    
    if (!response.ok) {
      if (response.status === 404) {
        return null; // Producto no encontrado
      }
      throw new Error(`Error HTTP: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || 'Error al obtener producto');
    }
    
    // El producto ya viene mapeado con imagen real desde la API
    return data.data.product;
  } catch (error) {
    console.error(`Error obteniendo producto ${id}:`, error);
    return null;
  }
};

// Función para obtener múltiples productos por IDs
const fetchProductsByIds = async (ids: number[], lang: string = 'es'): Promise<ProductDetail[]> => {
  const products: ProductDetail[] = [];
  
  // Obtener productos en paralelo
  const productPromises = ids.map(id => fetchProductById(id, lang));
  const results = await Promise.all(productPromises);
  
  // Filtrar productos nulos y agregar a la lista
  results.forEach(product => {
    if (product) {
      products.push(product);
    }
  });
  
  return products;
};

export const useLoMasVendidoProducts = (lang: string = 'es') => {
  const { data: strapiData, loading: strapiLoading, error: strapiError } = useStrapiHome();

  return useQuery({
    queryKey: ['loMasVendidoProducts', lang, strapiData?.loMasVendido?.productos],
    queryFn: async () => {
      if (!strapiData?.loMasVendido?.productos) {
        return [];
      }

      // Extraer IDs válidos de productos de PrestaShop
      const validProductIds = strapiData.loMasVendido.productos
        .filter(producto => producto.id_producto_prestashop !== null)
        .map(producto => producto.id_producto_prestashop as number);

      if (validProductIds.length === 0) {
        return [];
      }

      return await fetchProductsByIds(validProductIds, lang);
    },
    enabled: !strapiLoading && !!strapiData?.loMasVendido?.productos,
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 10 * 60 * 1000, // 10 minutos
  });
};

export default useLoMasVendidoProducts;
